/**
 * Detects whether the current session is running inside an installed mobile app / PWA / WebView
 * versus running on the regular public website.
 */
export function isMobileAppEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  // 0. Check session cache if already determined
  try {
    const cached = window.sessionStorage?.getItem('he_is_mobile_app');
    if (cached === 'true') return true;
  } catch {
    // ignore
  }

  // 1. Check standalone display mode (installed PWA / APK TWA / Android Chrome webapp)
  const isStandalone = 
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.matchMedia('(display-mode: minimal-ui)').matches ||
    (window.navigator as any).standalone === true;

  if (isStandalone) {
    try { window.sessionStorage?.setItem('he_is_mobile_app', 'true'); } catch {}
    return true;
  }

  // 2. Check query params like ?platform=app or ?app=true or ?source=apk
  const urlParams = new URLSearchParams(window.location.search);
  if (
    urlParams.get('platform') === 'app' || 
    urlParams.get('platform') === 'android' || 
    urlParams.get('app') === 'true' || 
    urlParams.get('source') === 'apk' ||
    urlParams.get('source') === 'app' ||
    urlParams.get('mode') === 'app' ||
    urlParams.get('isApp') === 'true'
  ) {
    try { window.sessionStorage?.setItem('he_is_mobile_app', 'true'); } catch {}
    return true;
  }

  // 3. Check referrer from an Android app
  if (document.referrer && document.referrer.includes('android-app://')) {
    try { window.sessionStorage?.setItem('he_is_mobile_app', 'true'); } catch {}
    return true;
  }

  // 4. Check Android WebView user-agent flags or Capacitor
  const ua = window.navigator.userAgent || '';
  if (
    /; wv\)/i.test(ua) || 
    (/(Android.*Version\/[0-9.]+\s+Chrome\/[0-9.]+)/i.test(ua) && !/Mobile Safari/i.test(ua)) ||
    /Capacitor/i.test(ua) ||
    /Cordova/i.test(ua)
  ) {
    try { window.sessionStorage?.setItem('he_is_mobile_app', 'true'); } catch {}
    return true;
  }

  // 5. Check native bridge injections
  if ((window as any).Capacitor?.isNativePlatform?.() || (window as any).isNativeApp || (window as any).AndroidBridge) {
    try { window.sessionStorage?.setItem('he_is_mobile_app', 'true'); } catch {}
    return true;
  }

  return false;
}

export function isWebsiteEnvironment(): boolean {
  return !isMobileAppEnvironment();
}
